// src/features/box/AllBoxes.tsx
import React, {useMemo, useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {Routes} from '../../common/Routes'
import {Box as UiBox} from '../../webui/Box'
import {Container} from '../../webui/Container'
import {Link} from '../../webui/Link'
import {Paper} from '../../webui/Paper'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'

import {BoxMap} from './map/BoxMap'
import {BoxMarker} from './map/types/boxMarker'

import {BoxLite} from '../../common/types/box'
import {GQL_BoxesPageViewForLiteQuery, useBoxesPageViewForLiteQuery} from '../../generated/graphql'
import {transformBoxColor} from '../../gql/transformBox'
import {useAllOrganisations} from '../organisation/gql/useAllOrganisations'
import {BoxesPageFilterState} from './BoxesPageFilter'
import {BoxesTable} from './BoxesTable'

const DEFAULT_PRICE_WEIGHT = {
  id: 'default',
  name: '',
  start: 0,
  firstHour: 0,
  secondHour: 0,
  thirdHour: 0,
  fourthHour: 0,
  fifthHour: 0,
  sixthHour: 0,
  penalty: 0,
}

const HARD_LIMIT = 2000
type BoxesPageViewRow = NonNullable<NonNullable<GQL_BoxesPageViewForLiteQuery['box']>['boxesPageView']>[number]

const initialFilterState: BoxesPageFilterState = {
  open: false,
  search: '',
  activeMode: 'all',
  tempClosedStatus: 'all',
  deprecatedMode: 'exclude',
  contractTypes: [],
  includeContractTypeNotSet: false,
}

export const AllBoxes: React.FC = () => {
  const {data, loading, error} = useBoxesPageViewForLiteQuery({
    variables: {onlyActive: false, offset: 0, limit: HARD_LIMIT},
    fetchPolicy: 'cache-first',
  })

  /**
   * NOTE (server-side improvement):
   *
   * boxesPageView does not expose organisation (or organisationId) on Box,
   * so we currently fetch *all organisations* and derive the organisation name
   * by scanning organisation.boxes to find matching box ids.
   *
   * This works but is inefficient:
   *  - transfers more data than needed
   *  - requires O(orgs × boxes) client-side matching
   *  - duplicates logic already implicit in the backend data model
   *
   * Desired server change:
   *  - Expose either `organisationId` or a minimal
   *    `organisation { id name }` field on Box in boxesPageView.
   *
   * With that, this page could fetch organisation data directly in the
   * boxesPageView query and drop the secondary organisations query entirely.
   */

  const {data: allOrganisations, loading: orgsLoading, error: orgsError} = useAllOrganisations({})

  const [filter, setFilter] = useState<BoxesPageFilterState>(initialFilterState)

  const onClearFilter = () => setFilter(initialFilterState)

  const boxesGql = useMemo(() => (data?.box?.boxesPageView as BoxesPageViewRow[]) ?? [], [data])

  const hitLimit = boxesGql.length >= HARD_LIMIT

  const boxIdToOrganisationName = useMemo(() => {
    const map = new Map<string, string>()

    if (!allOrganisations) return map

    for (const o of allOrganisations) {
      const orgName = o.name ?? ''
      const boxes = o.boxes ?? []
      for (const b of boxes) {
        if (b?.id) map.set(b.id, orgName)
      }
    }

    return map
  }, [allOrganisations])

  const filteredBoxesGql = useMemo(() => {
    const q = filter.search.trim().toLowerCase()

    return boxesGql.filter((b) => {
      if (filter.deprecatedMode === 'exclude' && b.deprecated) return false
      if (filter.deprecatedMode === 'only' && !b.deprecated) return false

      if (filter.activeMode === 'onlyActive' && !b.active) return false
      if (filter.activeMode === 'excludeActive' && b.active) return false

      if (filter.tempClosedStatus === 'yes' && !b.temporarilyClosed) return false
      if (filter.tempClosedStatus === 'no' && b.temporarilyClosed) return false

      const hasContractTypeFilter = filter.contractTypes.length > 0 || filter.includeContractTypeNotSet

      if (hasContractTypeFilter) {
        const matchNotSet = b.contractType == null && filter.includeContractTypeNotSet
        const matchSet = b.contractType != null && filter.contractTypes.includes(b.contractType)

        if (!matchNotSet && !matchSet) return false
      }

      if (q) {
        const orgName = boxIdToOrganisationName.get(b.id) ?? ''
        const hay = `${b.name ?? ''} ${b.circuitBoardId ?? ''} ${orgName}`.toLowerCase()
        if (!hay.includes(q)) return false
      }

      return true
    })
  }, [boxesGql, filter, boxIdToOrganisationName])
  const boxMarkers: BoxMarker[] = useMemo(
    () =>
      filteredBoxesGql
        .filter((b) => !!b.location?.coordinates)
        .map((b) => ({
          id: b.id,
          title: b.name,
          active: b.active,
          color: transformBoxColor(b.color),
          coordinates: b.location!.coordinates!,
          organisationName: boxIdToOrganisationName.get(b.id) ?? '',
          locationName: b.location.name ?? '',
        })),
    [filteredBoxesGql, boxIdToOrganisationName],
  )

  const boxesForTable: BoxLite[] = useMemo(
    () =>
      filteredBoxesGql.map((b) => ({
        id: b.id,
        name: b.name,
        active: b.active,
        deprecated: b.deprecated,
        temporarilyClosed: b.temporarilyClosed,
        color: transformBoxColor(b.color),
        circuitBoardId: b.circuitBoardId,
        serialNumber: b.serialNumber ?? '',
        openStartTime: '',
        openEndTime: '',
        initialActivationTime: b.initialActivationTime ?? '',
        sponsor: b.sponsor ? {id: b.sponsor.id, name: b.sponsor.name, imageName: ''} : undefined,
        priceWeight: DEFAULT_PRICE_WEIGHT,
        location: {
          id: b.location?.id ?? 'unknown',
          name: b.location?.name ?? undefined,
          coordinates: b.location?.coordinates ?? undefined,
        },
        compartments: b.compartments.map((c) => ({
          id: c.id,
          name: c.name,
          locked: c.locked ?? false,
          private: c.private ?? false,
          circuitBoardPort: 0 as any,
          ongoingRental: c.ongoingRental ? {id: c.ongoingRental.id, rentStart: c.ongoingRental.rentStart ?? ''} : undefined,
          product: undefined as any,
        })),
        contractType: b.contractType ?? null,
        organisationName: boxIdToOrganisationName.get(b.id) ?? '',
      })),
    [filteredBoxesGql, boxIdToOrganisationName],
  )

  if (loading) return <LoadingSpinnerContainer />
  if (error) return <div>{error.message}</div>

  const orgStatusText = orgsError ? 'Organisation lookup failed' : orgsLoading ? 'Loading organisations…' : null

  return (
    <Container maxWidth={false} disableGutters sx={{px: 2}}>
      <UiBox p={2} display="flex" flexDirection="row" justifyContent="space-between">
        <UiBox sx={{fontSize: '1.5rem', fontWeight: 600}}>Boxes</UiBox>
        <UiBox>
          <Link component={RouterLink} to={Routes.ADD_BOX}>
            Add new box
          </Link>
        </UiBox>
      </UiBox>

      {orgStatusText && (
        <Paper sx={{mt: 2}}>
          <UiBox p={1.5}>
            <UiBox sx={{color: 'text.secondary'}}>{orgStatusText}</UiBox>
          </UiBox>
        </Paper>
      )}

      {hitLimit && (
        <Paper sx={{mt: 2}}>
          <UiBox p={1.5}>
            <UiBox sx={{color: 'warning.main'}}>
              Showing the first {HARD_LIMIT} boxes. Some boxes may be omitted. Note to dev team: Consider adding pagination.
            </UiBox>
          </UiBox>
        </Paper>
      )}

      <Paper sx={{mt: 2}}>
        <UiBox sx={{mx: 'auto', width: 'fit-content', maxWidth: 1100}}>
          <UiBox sx={{width: 900}}>
            <BoxMap boxMarkers={boxMarkers} />
          </UiBox>
        </UiBox>
      </Paper>

      <BoxesTable boxes={boxesForTable} filter={filter} setFilter={setFilter} onClearFilter={onClearFilter} />
    </Container>
  )
}
